import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import "./assistant-chat.css";
import { fetchDirectory, sendChatMessage, submitLead as submitLeadApi, SableApiError } from "../api/sableApi";
import StorytellerExperience from "./StorytellerExperience";
import VoiceOrbExperience from "./VoiceOrbExperience";
import MiniSiteExperience from "./MiniSiteExperience";
import { useMatchDocumentBackground } from "./useMatchDocumentBackground";
// Generic, business-agnostic starters -- sable-api's directory payload has no per-business
// "topics" concept (that was a scripted-demo-only idea from the earlier mock build). Tapping one
// just sends it as a real chat message; the real brain decides how to answer it, same as anything
// typed by hand.
const QUICK_PROMPTS = ["What do you offer?", "Hours & location", "I'd like to schedule a call"];
function visitorKeyStorageKey(token) {
    return `sable-visitor-key-${token}`;
}
function recapStorageKey(token) {
    return `sable-chat-recap-${token}`;
}
function getOrCreateVisitorKey(token) {
    try {
        const existing = window.localStorage.getItem(visitorKeyStorageKey(token));
        if (existing)
            return existing;
        const fresh = crypto.randomUUID();
        window.localStorage.setItem(visitorKeyStorageKey(token), fresh);
        return fresh;
    }
    catch {
        // Private window / blocked storage -- an ephemeral key still lets the chat work for this one
        // page view, it just won't resume or recap on a later visit.
        return crypto.randomUUID();
    }
}
function businessShortName(business) {
    return business ? business.split(" · ")[0] : "The business";
}
export default function AssistantChatPage() {
    const { slug } = useParams();
    const token = slug ?? "";
    const [state, setState] = useState("loading");
    const [directory, setDirectory] = useState(null);
    // Applied here, the single shared entry point for every presentation_type (current and future --
    // chatbot/storyteller today, voice_orb overrides it with its own always-dark fix), so every
    // assistant/pals page gets this fix by default rather than each one adding it separately.
    // `#111111`/`#f4f4f2` must stay in sync with assistant-chat.css's own `--canvas: light-dark(...)`
    // values -- this is the same color the real on-screen canvas resolves to, just mirrored onto the
    // underlying document so iOS Safari's overscroll bounce doesn't flash the browser's default white
    // past it (see useMatchDocumentBackground's own doc comment for the full story).
    const prefersDarkCanvas = useMemo(() => window.matchMedia("(prefers-color-scheme: dark)").matches, []);
    useMatchDocumentBackground(prefersDarkCanvas ? "#111111" : "#f4f4f2");
    useEffect(() => {
        let cancelled = false;
        setState("loading");
        fetchDirectory(token)
            .then((dir) => {
            if (cancelled)
                return;
            if (dir.status === "disabled") {
                setState("disabled");
                return;
            }
            setDirectory(dir);
            setState("ready");
        })
            .catch((err) => {
            if (cancelled)
                return;
            setState(err instanceof SableApiError && err.status === 404 ? "unknown" : "error");
        });
        return () => {
            cancelled = true;
        };
    }, [token]);
    if (state === "loading")
        return _jsx(StatusPage, { token: token, title: "Loading", body: "Looking this card up." });
    if (state === "unknown")
        return _jsx(UnknownAssistant, { token: token });
    if (state === "error")
        return _jsx(StatusPage, { token: token, title: "Can't connect", body: "Couldn't reach this Assistant right now. Try again in a moment." });
    if (state === "disabled")
        return _jsx(DisabledCard, {});
    if (directory.presentationType === "storyteller") {
        return _jsx(StorytellerExperience, { token: token, directory: directory });
    }
    if (directory.presentationType === "voice_orb") {
        return _jsx(VoiceOrbExperience, { token: token, directory: directory });
    }
    if (directory.presentationType === "mini_site") {
        return _jsx(MiniSiteExperience, { token: token, directory: directory });
    }
    return _jsx(AssistantChat, { token: token, directory: directory });
}
function StatusPage({ token, title, body }) {
    return (_jsx("div", { className: "ac-root", children: _jsx("div", { className: "ac-shell", children: _jsx("div", { className: "ac-unknown", children: _jsxs("div", { style: { maxWidth: 320 }, children: [_jsxs("p", { className: "ac-mono", style: { fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--fg-2)", marginBottom: 12 }, children: ["sable / ", token] }), _jsx("h1", { children: title }), _jsx("p", { children: body })] }) }) }) }));
}
function UnknownAssistant({ token }) {
    return (_jsx(StatusPage, { token: token, title: "Not found", body: "This link doesn't match any Assistant card. If you scanned a QR code, try again, or contact the business another way." }));
}
// Copy from sable-design-handoff.zip's PROMPT.md §G / proposal §5.3 -- a neutral line, no mention
// of compromise or security, same type/ink-on-paper language as the rest of sable.
function DisabledCard() {
    return (_jsx("div", { className: "ac-root", children: _jsx("div", { className: "ac-shell", children: _jsx("div", { className: "ac-unknown", children: _jsxs("div", { style: { maxWidth: 320 }, children: [_jsx("h1", { children: "This card is no longer active." }), _jsx("p", { children: "Please contact the business another way for now." })] }) }) }) }));
}
function AssistantChat({ token, directory }) {
    const [phase, setPhase] = useState("directory");
    const [messages, setMessages] = useState([]);
    const [recap, setRecap] = useState(null);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [leadShown, setLeadShown] = useState(false);
    const [leadDismissed, setLeadDismissed] = useState(false);
    const [leadName, setLeadName] = useState("");
    const [leadEmail, setLeadEmail] = useState("");
    const [toast, setToast] = useState(null);
    const [cardWentDisabled, setCardWentDisabled] = useState(false);
    const visitorMessageCount = useRef(0);
    const messagesEndRef = useRef(null);
    const visitorKey = useMemo(() => getOrCreateVisitorKey(token), [token]);
    const rootStyle = useMemo(() => ({ "--accent": directory.accent ?? "#2f6f7d" }), [directory.accent]);
    // Returning-visitor recap (§7.1 state 4). The real conversation/session state lives server-side
    // in sable-api, keyed by (card, visitor_key) -- this is only a client-side cache of the last
    // exchange so the recap banner has something to show immediately, before/without asking the
    // backend to replay anything. A fresh visitor_key (see "Start over" below) is a real reset: it's
    // the same key sable-api uses to look up (or create) a VisitorSession.
    useEffect(() => {
        setPhase("directory");
        setMessages([]);
        setRecap(null);
        setCardWentDisabled(false);
        visitorMessageCount.current = 0;
        try {
            const raw = window.localStorage.getItem(recapStorageKey(token));
            if (raw) {
                const saved = JSON.parse(raw);
                setRecap(saved);
                setMessages([{ from: "assistant", text: saved.reply }]);
                setPhase("chat");
            }
        }
        catch {
            // Skip the recap, directory state still works.
        }
    }, [token]);
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [messages, leadShown, sending]);
    useEffect(() => {
        if (!toast)
            return;
        const t = setTimeout(() => setToast(null), 3200);
        return () => clearTimeout(t);
    }, [toast]);
    const saveRecap = (question, reply) => {
        try {
            window.localStorage.setItem(recapStorageKey(token), JSON.stringify({ question, reply }));
        }
        catch {
            // Recap is a nice-to-have, not required for the chat to work.
        }
    };
    const sendMessage = async (text) => {
        const trimmed = text.trim();
        if (!trimmed || sending)
            return;
        setInput("");
        if (phase === "directory")
            setPhase("chat");
        setMessages((prev) => [...prev, { from: "visitor", text: trimmed }]);
        setSending(true);
        try {
            const reply = await sendChatMessage(token, trimmed, visitorKey);
            setMessages((prev) => [...prev, { from: "assistant", text: reply }]);
            saveRecap(trimmed, reply);
            visitorMessageCount.current += 1;
            if (visitorMessageCount.current >= 3 && !leadDismissed)
                setLeadShown(true);
        }
        catch (err) {
            if (err instanceof SableApiError && err.status === 404) {
                // The card was disabled after this page loaded (or between scans) -- a real state change,
                // not a transient error, so it gets the full disabled-card page, not a toast.
                setCardWentDisabled(true);
            }
            else if (err instanceof SableApiError && err.status === 429) {
                setToast(err.message);
            }
            else {
                setToast("Couldn't send that. Try again in a moment.");
            }
        }
        finally {
            setSending(false);
        }
    };
    const submitLead = async () => {
        if (!leadName.trim() || !leadEmail.trim())
            return;
        try {
            await submitLeadApi(token, leadName.trim(), leadEmail.trim(), visitorKey);
            setLeadShown(false);
            setLeadDismissed(true);
            setToast(`Thanks. ${business} will reach out soon.`);
        }
        catch {
            setToast("Couldn't send that. Try again in a moment.");
        }
    };
    const backToDirectory = () => setPhase("directory");
    const startOver = () => {
        try {
            window.localStorage.removeItem(recapStorageKey(token));
            window.localStorage.removeItem(visitorKeyStorageKey(token));
        }
        catch {
            // Nothing to clean up if storage was never available.
        }
        window.location.reload();
    };
    if (cardWentDisabled)
        return _jsx(DisabledCard, {});
    const ownerName = directory.ownerName ?? "This Assistant";
    const business = businessShortName(directory.business);
    return (_jsxs("div", { className: "ac-root", style: rootStyle, children: [_jsxs("div", { className: "ac-shell", children: [phase === "directory" ? (_jsx(Directory, { directory: directory, onSchedule: () => setToast("Scheduling isn't connected yet. Use the phone number above for now.") })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "ac-chat-header", children: [_jsxs("button", { className: "ac-chat-header-row", onClick: backToDirectory, children: [_jsx("span", { className: "ac-avatar sm", children: initials(ownerName) }), _jsxs("span", { children: [_jsx("span", { className: "ac-owner-name", style: { display: "block" }, children: ownerName }), _jsx("span", { className: "ac-business", children: directory.business })] }), _jsx("span", { className: "ac-back", children: "\u2039 Info" })] }), _jsxs("p", { className: "ac-disclosure", children: ["I'm sable, ", ownerName, "'s AI assistant. ", business, " can see this conversation."] })] }), _jsxs("div", { className: "ac-messages", children: [recap && (_jsxs("div", { className: "ac-recap", children: [_jsx("span", { className: "ac-mono", children: "Welcome back" }), "You asked about ", _jsx("b", { children: recap.question }), ".", _jsx("button", { onClick: startOver, children: "Not you? Start over" })] })), messages.map((m, i) => (_jsx("div", { className: `ac-msg ${m.from}`, children: m.text }, i))), sending && _jsx("div", { className: "ac-msg assistant", children: "\u2026" }), leadShown && (_jsxs("div", { className: "ac-lead", children: [_jsx("button", { className: "ac-lead-close", "aria-label": "Dismiss", onClick: () => { setLeadShown(false); setLeadDismissed(true); }, children: "\u00D7" }), _jsxs("h3", { children: ["Want a reply from ", ownerName, " directly?"] }), _jsx("p", { children: "Optional. Leave your info and they'll follow up. The chat keeps working either way." }), _jsxs("div", { className: "ac-lead-row", children: [_jsx("input", { placeholder: "Name", value: leadName, onChange: (e) => setLeadName(e.target.value), "aria-label": "Name" }), _jsx("input", { placeholder: "Email", value: leadEmail, onChange: (e) => setLeadEmail(e.target.value), "aria-label": "Email" }), _jsx("button", { onClick: submitLead, children: "Share" })] })] })), _jsx("div", { ref: messagesEndRef })] })] })), phase === "chat" && (_jsx("div", { className: "ac-chips", role: "group", "aria-label": "Quick prompts", children: QUICK_PROMPTS.map((p) => (_jsx("button", { disabled: sending, onClick: () => sendMessage(p), children: p }, p))) })), _jsxs("form", { className: "ac-composer", onSubmit: (e) => {
                            e.preventDefault();
                            sendMessage(input);
                        }, children: [_jsx("input", { type: "text", value: input, onChange: (e) => setInput(e.target.value), placeholder: phase === "directory" ? "Ask me anything" : "Type a message", "aria-label": "Message", disabled: sending }), _jsx("button", { type: "submit", disabled: !input.trim() || sending, children: "Send" })] }), _jsxs("div", { className: "ac-footer", children: [_jsx("p", { children: "Powered by OffsetMind" }), phase === "chat" && _jsx("button", { onClick: startOver, children: "Start over" })] })] }), toast && _jsx("div", { className: "ac-toast", children: toast })] }));
}
function Directory({ directory, onSchedule }) {
    const ownerName = directory.ownerName ?? "This Assistant";
    return (_jsxs("div", { className: "ac-directory", children: [_jsxs("div", { className: "ac-brand-row", children: [_jsx("span", { className: "ac-avatar", children: initials(ownerName) }), _jsxs("div", { children: [_jsx("div", { className: "ac-owner-name", children: ownerName }), _jsx("div", { className: "ac-business", children: directory.business })] })] }), _jsx("p", { className: "ac-tagline", children: directory.tagline }), _jsxs("div", { className: "ac-info-list", children: [directory.hours && (_jsxs("div", { className: "ac-info-row", children: [_jsx("span", { className: "ac-label", children: "Hours" }), _jsx("span", { children: directory.hours })] })), directory.address && (_jsxs("div", { className: "ac-info-row", children: [_jsx("span", { className: "ac-label", children: "Address" }), _jsx("span", { children: directory.address })] })), directory.phone && (_jsxs("div", { className: "ac-info-row", children: [_jsx("span", { className: "ac-label", children: "Phone" }), _jsx("a", { href: `tel:${directory.phone.replace(/[^\d+]/g, "")}`, children: directory.phone })] })), directory.links.length > 0 && (_jsxs("div", { className: "ac-info-row", children: [_jsx("span", { className: "ac-label", children: "Links" }), _jsx("div", { className: "ac-links", children: directory.links.map((l) => (_jsx("a", { href: l.href, children: l.label }, l.label))) })] }))] }), _jsx("button", { className: "ac-cta", onClick: onSchedule, children: directory.scheduleLabel ?? "Schedule a call" }), _jsx("div", { className: "ac-ask-row", children: _jsx("div", { className: "ac-ask-hint", children: "Or ask me anything" }) })] }));
}
// Same shape as sable's own initials() (mini-brain-business-card-sites proposal §8.1's
// mockData.ts) -- last two words, so a leading title ("Pastor David Okafor") reads as "DO"
// instead of "PD".
function initials(name) {
    const words = name.split(" ");
    return words.slice(-2).map((w) => w[0]).join("");
}
